import React, { useEffect, useMemo, useRef, useState } from 'react'
import cx from 'classnames'
import styles from './MediathekModal.module.scss'
import { useTranslation } from 'react-i18next'
import { useFetchData } from '../../../hooks/useFetchData'
import { MediaCategory, MediaWithCategories } from '../../../types/Media'
import { ApiRoutes } from '../../../services/ApiService'
import { getTranslatedObject } from '../../../helpers/getTranslatedObject'
import { StoredSpot } from '../../../types/Individualization'
import { treeOptions } from '../../../helpers/treeOptions'
import Pagination from '../../../components/Pagination'
import Select from '../../../components/Select'
import Icon from '../../../components/Icon'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import Loader from '../../../components/Loader'
import { usePluginContext } from '../../../contexts/PluginContext'

const MediathekModal: React.FC<{
    spot: StoredSpot
    uid: string
    selectImage: (media: MediaWithCategories) => void
    itemsPerPage?: number
    showImageTitle?: boolean
}> = ({ spot, selectImage, itemsPerPage = "16", showImageTitle = false }) => {
    const { t } = useTranslation(['customizer'])
    const { plugin } = usePluginContext()
    const currentPage = useRef<string>('1')

    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([])

    const catParam = useMemo(() => {
        if (!selectedCategoryIds.length) {
            return spot.media_category_filtered
                ? spot.media_category_ids.join(',')
                : ''
        }
        return selectedCategoryIds.join(',')
    }, [spot, selectedCategoryIds])

    const {
        data: media,
        fetch,
        isLoading,
        pagination,
    } = useFetchData<MediaWithCategories>(ApiRoutes.MEDIA)

    const { data: categories, fetch: fetchCategories } =
        useFetchData<MediaCategory>(ApiRoutes.MEDIA_CATEGORIES)

    useEffect(() => {
        currentPage.current = '1'

        fetch({
            page: '1',
            categoryIds: catParam,
            perPage: itemsPerPage.toString(),
        })
    }, [catParam])

    const changePage = (p: number) => {
        currentPage.current = p.toString()

        fetch({
            page: p.toString(),
            categoryIds: catParam,
            perPage: itemsPerPage.toString(),
        })
    }

    useEffect(() => {
        fetchCategories()
    }, [fetchCategories])

    const translatedCategories = useMemo(() => {
        if (categories)
            return categories.map((category) =>
                getTranslatedObject(category, plugin?.language)
            )
        return []
    }, [categories, plugin])

    const selectOptions = useMemo(() => {
        if (!translatedCategories.length) return []
        if (spot.media_category_filtered) {
            return treeOptions<MediaCategory>(
                translatedCategories.filter((category) => {
                    const spotMediaCategoryIds = spot.media_category_ids.map(
                        (id) => Number(id)
                    )
                    return spotMediaCategoryIds.includes(Number(category.id))
                }),
                null,
                'name',
                'id',
                'parent_id',
                'id',
                0
            )
        }

        return treeOptions<MediaCategory>(
            translatedCategories,
            null,
            'name',
            'id',
            'parent_id',
            'id',
            0
        )
    }, [translatedCategories, spot])

    const onCategorySelect = (categoryId: number) => {
        currentPage.current = '1'

        if (selectedCategoryIds.includes(categoryId)) {
            setSelectedCategoryIds((prev) =>
                prev.filter((id) => id !== categoryId)
            )
        } else {
            setSelectedCategoryIds((prev) => [...prev, categoryId])
        }
    }

    return (
        <div className={cx(styles['zr-modal-content-wrapper'], 'zr-w-100')}>
            <Select
                options={selectOptions}
                onSelect={(_: string, value: string) =>
                    onCategorySelect(parseInt(value))
                }
                property="category"
                value={selectedCategoryIds
                    .map((id) => id.toString())
                    .join(',')}
                placeholder={t('mediaFilterByCategory')}
                classes={ 'zr-mb-1 zr-w-100' }
                searchable={false}
                searchTermUpdate={false}
                clearable={false}
            />
            <div
                className={'zr-w-100 zr-d-flex zr-gap-2 zr-mb-4'}
                style={{ flexWrap: 'wrap' }}
            >
                {selectedCategoryIds.map((id) => {
                    const category = translatedCategories.find(
                        (cat) => cat.id === id
                    )
                    return (
                        <div
                            className={cx(
                                styles['zr-selected-category-badge']
                            )}
                            key={id}
                        >
                            <div>{category?.name}</div>
                            <Icon
                                icon={faXmark}
                                color={'#2f2f2f'}
                                onClick={() => onCategorySelect(Number(id))}
                            />
                        </div>
                    )
                })}
            </div>

            {isLoading ? (
                <div className={'zr-d-flex zr-justify-center'}><Loader isLoading={isLoading} /></div>
            ) : media.length === 0 ? (
                <p>{t('noMediaFound')}</p>
            ) : (
                <div className={cx(styles['zr-mediathek-wrapper'])}>
                    <div className={cx(styles['zr-mediathek-elements-wrapper'])}>
                        {media.map((mediaItem) => (
                            <div
                                key={mediaItem.id}
                                onClick={() => selectImage(mediaItem)}
                            >
                                <img
                                    width={'100%'}
                                    src={mediaItem.path}
                                    alt={mediaItem.name}
                                    style={{ objectFit: 'contain' }}
                                />
                                {showImageTitle && (
                                    <span
                                        className={cx(
                                            styles['zr-media-item-title']
                                        )}
                                    >
                                        {mediaItem.name}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                    <div>
                        {pagination && pagination.lastPage > 1 && (
                            <Pagination
                                totalPages={pagination.lastPage}
                                currentPage={Number(
                                    currentPage.current
                                )}
                                onPageChange={(p: number) =>
                                    changePage(p)
                                }
                                classes={'zr-mt-4'}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default MediathekModal
